import { useFormState, useStep } from '@flywire/react-hooks';
import type { DocumentsCollection } from '@packages/domain/models/documentsCollection';
import { useQuery } from '@tanstack/react-query';
import { fetchDocumentsCollections } from 'actions/entities/payments';
import { getSteps } from 'components/LRS/steps';
import type { Transaction } from 'models/transaction';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  allFilesAnalyzed,
  hasFilesInDocVerifyProcess,
} from 'selectors/entities/documents/documents';
import { payex } from 'services/payex';
import type { I18n } from 'utils';

type LRSProviderProps = {
  allCollectionFilesUploaded: boolean;
  children: React.ReactNode;
  documentsCollection: DocumentsCollection[];
  i18n: I18n;
  idVerificationNeeded: boolean;
  initialValues: Record<string, string>;
  isStudentPaying: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  paymentId: string;
  previousTransactions: Transaction[];
};

type LRSContextProps = {
  allCollectionFilesUploaded: boolean;
  filesInDocVerifyProcess: boolean;
  complete: () => void;
  completed: string[];
  documentsCollection: DocumentsCollection[];
  i18n: I18n;
  idVerificationNeeded: boolean;
  index: number;
  isStudentPaying: boolean;
  navigation: () => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
  onSubmit: () => void;
  previousTransactions: Transaction[];
  steps: { id: string; element: string }[];
  updateAll: () => void;
  values: Record<string, unknown>;
};

const FIFTEEN_SECONDS = 15000;
const LRSContext = createContext<LRSContextProps | undefined>(undefined);

function LRSProvider({
  allCollectionFilesUploaded = false,
  children,
  documentsCollection = [],
  i18n,
  idVerificationNeeded = false,
  initialValues = {},
  isStudentPaying = false,
  onCancel,
  onSubmit,
  paymentId,
  previousTransactions = [],
}: LRSProviderProps) {
  const dispatch = useDispatch();
  const { values, update, updateAll, dirtyFields } = useFormState({
    ...initialValues,
  });

  const steps = getSteps();
  const { index, navigation, complete, completed } = useStep({
    initialStep: 0,
    steps,
  });
  const [newFilesAnalyzed, setNewFilesAnalyzed] = useState(false);
  const [docVerifyFinished, setDocVerifyFinished] = useState(false);
  const filesInDocVerifyProcess = useSelector(hasFilesInDocVerifyProcess);
  const allAnalyzed = useSelector(allFilesAnalyzed);

  useQuery({
    queryKey: ['documentPolling', paymentId],
    queryFn: async () => {
      const response =
        await payex.polling.pollPaymentDocumentsAnalyzed(paymentId);

      setNewFilesAnalyzed(response.documentsAnalyzed);
      if (response.docVerifyFinished !== undefined) {
        setDocVerifyFinished(response.docVerifyFinished);
      }
    },
    refetchInterval: FIFTEEN_SECONDS,
    enabled: !allAnalyzed,
  });

  useEffect(() => {
    if (newFilesAnalyzed === true || docVerifyFinished === true) {
      dispatch(fetchDocumentsCollections());
    }
  }, [newFilesAnalyzed, docVerifyFinished, dispatch]);

  const onNext = useCallback(
    (field, data, nextStep) => {
      update(field, data);
      complete(index);

      if (nextStep) {
        return navigation.go(nextStep);
      }
      navigation.next();
    },
    [update, complete, index, navigation],
  );

  const onBack = useCallback(() => navigation.go('summary'), [navigation]);

  const value = useMemo(
    () => ({
      allCollectionFilesUploaded,
      filesInDocVerifyProcess,
      complete,
      completed,
      dirtyFields,
      documentsCollection,
      i18n,
      idVerificationNeeded,
      index,
      isStudentPaying,
      navigation,
      onBack,
      onCancel,
      onNext,
      onSubmit,
      paymentId,
      previousTransactions,
      steps,
      updateAll,
      values,
    }),
    [
      allCollectionFilesUploaded,
      filesInDocVerifyProcess,
      complete,
      completed,
      dirtyFields,
      documentsCollection,
      i18n,
      idVerificationNeeded,
      index,
      isStudentPaying,
      navigation,
      onBack,
      onCancel,
      onNext,
      onSubmit,
      paymentId,
      previousTransactions,
      steps,
      updateAll,
      values,
    ],
  );

  return <LRSContext.Provider value={value}>{children}</LRSContext.Provider>;
}

function useLRSContext() {
  const context = useContext(LRSContext);

  if (context === undefined) {
    throw new Error('useLRSContext must be used within a LRSProvider');
  }

  return context;
}

export { LRSContext, LRSProvider, useLRSContext };
